import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getMessagesByContactIdWithPagination, sendMessage, sendFile, sendAudioMessage } from '../services/MessageService';
import { getContacts, getContactsWithFilters, mapApiContactToContact, getContactCounts, updateContact, createContact, deleteContact, archiveContact, markContactAsViewed, getContactById, DuplicateContactError, getMessages } from '../services/ContactService';
import type { Contact as ApiContact } from '../services/ContactService';
// NOTE: chat now uses /Contact/counts/* , /Contact/contacts/list (POST), /Notification/poll/* (and v1/Contact, v1/Notification variants) + {data:...} / {message,code,data} shapes. Messages via Contact/* paths for consistency.
import { getAllUsers } from '../services/UserService';
import { getTags } from '../services/LabelService';
import { getSector, getWhatsAppGroups, WhatsAppGroupDto } from '../services/SectorService';
import { toggleContactAI } from '../services/AIAgentService';
import SessionService from '../services/SessionService';
import { AudioRecorder } from '../components/AudioRecorder';
import { ContactItem } from '../components/ContactItem';
import { AudioMessage } from '../components/AudioMessage';
import ChatWebSocketService from '../services/ChatWebSocketService';
import { InboxAiAssist, isMendsVisionEnabled } from '../mendsAi';
import { openRegisterConversation } from '../mendsAi/openRegister';
import {
  EmailTimelineBubble,
  EmailReplyComposer,
  ReplyChannelSwitch,
  ChannelFilterSelect,
  useContactEmails,
  EmailHistoryNotice,
  mergeConversationTimeline,
  matchesChannelFilter,
  isEmailOnlyContact,
  channelFilterOptions,
  defaultReplyChannel,
  needsInstagramReconnect,
} from '../components/emailConversation';
import type { ConversationChannelFilter, ReplyChannel } from '../components/emailConversation';
import { ChannelBadge, MetaReplyComposer, InstagramReconnectNotice, WhatsAppWindowNotice, WhatsAppDeliveryNote, sendFailureText, useMetaChannels, useWebchatSite, metaMessageDisplay } from '../components/metaConversation';
import { whatsAppComposerLocked, WHATSAPP_TEMPLATE_ONLY_TEXT } from '../components/metaConversation/whatsAppWindow';
import type { ContactEmailItem } from '../services/ContactEmailService';
import { rememberActiveContact } from '../mendsAi/lib/activeContact';
import { readChatLink, type ChatLinkFilter } from '../utils/chatDeepLink';
import dayjs from 'dayjs';
import { isOperationalFilter, matchesOperationalFilter } from '../utils/chatOpFilters';
import { FiMoreVertical, FiUserPlus, FiTag, FiUsers, FiEdit2, FiArrowLeft, FiArchive, FiCpu, FiPaperclip, FiMic, FiSend, FiSearch, FiVideo, FiX, FiMenu } from 'react-icons/fi';
import { ImSpinner2 } from 'react-icons/im';
import { ConfirmModal } from '../components/SharedModal';
import InternalSystemMessage from '../components/InternalSystemMessage';
import './ChatMobile.css';
import '../styles/components/ContactItem.css';
import { getContactPickerPrimaryLabel, isLidOnlyAddress, dedupeChatContactsForList, mergeChatListFirstPageFromPoll, resolveContactDisplayName } from '../utils/formatters';
import { isOptimisticOutgoing, matchesOptimisticSent, matchesPendingOptimisticContent, markOptimisticAsSent } from '../utils/chatOptimisticMatch';
import { formatSparkOriginLabel, isSparkContact, pickSparkFieldsFromRealtime } from '../utils/sparkOriginLabel';
import { coalesceMediaUrl, enrichAudioMessageWithPendingBlob, getDisplayCaption } from '../utils/messageMedia';
import { apiErrorMessage } from '../utils/apiErrorMessage';
import { Virtuoso } from 'react-virtuoso';

const CHAT_CONTACTS_PAGE_SIZE = 50;

// Interfaces e tipos (mesmas do ChatWeb)
interface OriginalContactData {
  id: number;
  name: string;
  tagId: number | null;
  number: string;
  avatarUrl: string | null;
  email: string;
  notes: string | null;
  sectorId: number;
  isActive: boolean;
  priority: string;
  contactStatus: string;
  aiActive: number;
  aiAgentName?: string | null;
  assignedTo: number | null;
  assignedToName?: string | null;
  createdAt: string;
  profilePicture?: string;
  lastMessage?: string;
  lastMessageTime?: string;
  unreadCount?: number;
  is_viewed: boolean;
  archived?: boolean;
  isFavorite?: boolean;
  isGroup?: boolean;
  updatedAt?: string;
  sparkChatId?: string | null;
  sparkEntrypointId?: string | null;
  sparkLinePhone?: string | null;
  sparkLineLabel?: string | null;
  channel?: 'spark' | null;
  lastMessageChannel?: 'email' | 'whatsapp' | 'instagram' | 'messenger' | 'webchat' | null;
  hasEmail?: boolean;
  hasInstagram?: boolean;
  hasMessenger?: boolean;
  hasWebchat?: boolean;
  hasWhatsApp?: boolean;
}

interface Message {
  id: number;
  content: string | null;
  mediaType: string;
  mediaUrl: string | null;
  fileName: string | null;
  mimeType: string | null;
  sectorId: number;
  contactID: number;
  sentAt: string;
  isSent: boolean;
  isRead: boolean;
  status?: 'sending' | 'sent' | 'error';
  senderName?: string;
  /** Instagram/Messenger: selo da conta e estado (apagada/editada). Ausentes no WhatsApp. */
  channel?: 'instagram' | 'messenger' | 'webchat';
  accountLabel?: string;
  /** deleted · edited (Meta) ou o estado de entrega do WhatsApp oficial: sent · delivered · read · played · failed. */
  state?: 'deleted' | 'edited' | 'sent' | 'delivered' | 'read' | 'played' | 'failed';
  /** Motivo em português quando o envio não foi entregue (WhatsApp oficial). */
  deliveryError?: string;
}

type ContactData = OriginalContactData;

function normalizeGroupJidKey(value?: string | null): string {
  if (!value) return '';
  const v = value.trim().toLowerCase();
  if (v.includes('@g.us')) return v;
  const digits = v.replace(/\D/g, '');
  return digits || v;
}

function apiAccToRows(contactsListRaw: ApiContact[], sectorId: number, groupsRes: { data?: WhatsAppGroupDto[] }): ContactData[] {
  const contactsList = contactsListRaw.map((c: ApiContact) => ({
    id: c.id,
    name: c.name,
    number: c.number,
    avatarUrl: c.avatarUrl,
    email: c.email,
    notes: c.notes,
    tagId: c.tagId,
    sectorId: c.sectorId,
    isActive: c.isActive,
    priority: c.priority,
    contactStatus: c.contactStatus,
    aiActive: c.aiActive,
    aiAgentName: c.aiAgentName ?? null,
    assignedTo: c.assignedTo,
    createdAt: c.createdAt,
    updatedAt: c.updatedAt,
    is_viewed: c.is_viewed ?? c.isViewed,
    archived: c.archived,
    isFavorite: c.isFavorite,
    lastMessage: c.lastMessage,
    lastMessageTime: c.lastMessageTime,
    unreadCount: c.unreadCount,
    isGroup: c.isGroup ?? false,
    sparkChatId: c.sparkChatId ?? null,
    sparkEntrypointId: c.sparkEntrypointId ?? null,
    sparkLinePhone: c.sparkLinePhone ?? null,
    sparkLineLabel: c.sparkLineLabel ?? null,
    channel: c.channel ?? null,
    lastMessageChannel: c.lastMessageChannel ?? null,
    hasEmail: c.hasEmail ?? false,
    hasInstagram: c.hasInstagram ?? false,
    hasMessenger: c.hasMessenger ?? false,
    hasWebchat: c.hasWebchat ?? false,
    hasWhatsApp: c.hasWhatsApp,
  }));
  const groups = groupsRes?.data || [];
  if (groups.length > 0) {
    const existingGroupKeys = new Set(
      contactsList.filter(c => c.isGroup).map(c => normalizeGroupJidKey(c.number))
    );
    const waGroupContacts = groups
      .filter(g => !existingGroupKeys.has(normalizeGroupJidKey(g.jid)))
      .map((g) => ({
        id: g.contactId ?? g.id,
        name: g.name,
        number: g.jid,
        avatarUrl: g.avatarUrl || null,
        email: '',
        notes: '',
        tagId: null,
        sectorId,
        isActive: true,
        priority: 'medium',
        contactStatus: 'active',
        aiActive: 0,
        assignedTo: null,
        createdAt: new Date().toISOString(),
        is_viewed: true,
        isGroup: true,
        lastMessage: g.lastMessage,
        lastMessageTime: g.lastMessageTime,
      }));
    return dedupeChatContactsForList([...contactsList, ...waGroupContacts]);
  }
  return dedupeChatContactsForList(contactsList);
}

type ChatMobileProps = {
  hasNewMessages?: { [contactId: number]: boolean };
  setHasNewMessages?: (newState: { [contactId: number]: boolean }) => void;
  contacts?: ContactData[];
  setContacts?: React.Dispatch<React.SetStateAction<ContactData[]>>;
  loadMoreContacts?: () => void;
  hasMoreContacts?: boolean;
  isLoadingMoreContacts?: boolean;
  totalContacts?: number;
  setUnreadCount?: (updater: number | ((prev: number) => number)) => void;
};

// Componentes de ícones
const ImageIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="9" cy="9" r="2" />
    <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
  </svg>
);

const DocumentIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
    <polyline points="14,2 14,8 20,8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10,9 9,9 8,9" />
  </svg>
);

const MicIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="22" />
  </svg>
);

const SendIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
  </svg>
);

const ChatMobile: React.FC<ChatMobileProps> = ({
  hasNewMessages: externalHasNewMessages,
  setHasNewMessages,
  contacts: externalContacts,
  setContacts: externalSetContacts,
  setUnreadCount
}) => {
  // Estados principais
  const [selectedContact, setSelectedContact] = useState<ContactData | null>(null);
  const pendingDraftRef = useRef<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messageInput, setMessageInput] = useState('');
  const [contacts, setContacts] = useState<ContactData[]>(externalContacts || []);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [chatHasMore, setChatHasMore] = useState(false);
  const [chatLoadingMore, setChatLoadingMore] = useState(false);
  const chatNextOffsetRef = useRef(0);
  const chatLoadLockRef = useRef(false);
  const [selectedSectorId, setSelectedSectorId] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isMessagesLoading, setIsMessagesLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [initialLoad, setInitialLoad] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [hasNewMessages, setLocalHasNewMessages] = useState<{ [contactId: number]: boolean }>(externalHasNewMessages || {});

  const skipExternalContactsMergeRef = useRef(false);
  const externalSetContactsRef = useRef(externalSetContacts);
  useEffect(() => {
    externalSetContactsRef.current = externalSetContacts;
  }, [externalSetContacts]);

  // Estados específicos do mobile
  const [showContactsList, setShowContactsList] = useState(true);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');

  // Estados para funcionalidades
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedTagId, setSelectedTagId] = useState<number | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    if (!externalContacts?.length) return;
    if (skipExternalContactsMergeRef.current) {
      skipExternalContactsMergeRef.current = false;
      return;
    }
    setContacts(prev => mergeChatListFirstPageFromPoll(prev, externalContacts));
  }, [externalContacts]);

  useEffect(() => {
    const push = externalSetContactsRef.current;
    if (!push) return;
    const ext = externalContacts || [];
    const sigC = contacts.map(c => c.id).join(',');
    const sigE = ext.map((c: ContactData) => c.id).join(',');
    if (contacts.length === ext.length && sigC === sigE) return;
    skipExternalContactsMergeRef.current = true;
    push(contacts);
  }, [contacts, externalContacts]);

  // Quando a lista é atualizada pelo polling (ex.: IA transferiu para humano), manter selectedContact em sync com aiActive
  useEffect(() => {
    if (!selectedContact || !externalContacts?.length) return;
    const fromList = externalContacts.find((c: any) => c.id === selectedContact.id) as { id: number; aiActive?: number; aiAgentName?: string | null } | undefined;
    const listAi = fromList && ('aiActive' in fromList ? fromList.aiActive : (fromList as any).AiActive);
    const listAgent = fromList?.aiAgentName ?? null;
    const aiChanged = fromList != null && listAi !== undefined && listAi !== selectedContact.aiActive;
    const agentChanged = fromList != null && listAgent !== (selectedContact.aiAgentName ?? null);
    if (aiChanged || agentChanged) {
      setSelectedContact(prev => prev ? { ...prev, aiActive: aiChanged ? (listAi as number) : prev.aiActive, aiAgentName: listAgent } : null);
    }
  }, [externalContacts, selectedContact?.id, selectedContact?.aiActive, selectedContact?.aiAgentName]);
  const [isTransferLoading, setIsTransferLoading] = useState(false);
  const [isTagLoading, setIsTagLoading] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [tags, setTags] = useState<any[]>([]);
  const [currentUserId, setCurrentUserId] = useState<number | undefined>(SessionService.getCurrentUserId() || undefined);
  const [isCurrentUserAdmin, setIsCurrentUserAdmin] = useState(SessionService.isCurrentUserAdmin());

  // Estados para filtros e modais
  const [showArchivedContacts, setShowArchivedContacts] = useState(false);
  const [pendingContactId, setPendingContactId] = useState<number | null>(null);
  const [archivedContactsCount, setArchivedContactsCount] = useState(0);
  const [showNewContactModal, setShowNewContactModal] = useState(false);
  const [newContactName, setNewContactName] = useState('');
  const [newContactNumber, setNewContactNumber] = useState('');
  const [isCreatingContact, setIsCreatingContact] = useState(false);
  const [newContactError, setNewContactError] = useState('');
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [selectedFilterUser, setSelectedFilterUser] = useState<number | null>(null);
  const [selectedFilterTag, setSelectedFilterTag] = useState<number | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  /** Fila operacional (HJ-02 / UX-02) — mesma semântica do ChatWeb. */
  const [activeFilter, setActiveFilter] = useState<ChatLinkFilter | 'all'>('all');
  const visionEnabled = isMendsVisionEnabled();
  // E-mail em Conversas: filtro por canal, canal da resposta e e-mail respondido (mesmo desenho do ChatWeb).
  const [channelFilter, setChannelFilter] = useState<ConversationChannelFilter>('all');
  const [replyChannel, setReplyChannel] = useState<ReplyChannel>('whatsapp');
  const [emailReplyTo, setEmailReplyTo] = useState<ContactEmailItem | null>(null);
  const [emailDraftSeed, setEmailDraftSeed] = useState<string | undefined>(undefined);
  const contactEmails = useContactEmails(
    selectedSectorId ? Number(selectedSectorId) : null,
    selectedContact && !selectedContact.isGroup ? selectedContact.id : null,
  );
  const emailOnly = isEmailOnlyContact(selectedContact);
  const emailAvailable = !contactEmails.forbidden && (contactEmails.status?.connected ?? false);
  // Instagram: só quando o setor tem conta ativa e o cliente tem a identidade do Instagram.
  const { instagramActive, messengerActive, whatsAppCloudActive } = useMetaChannels(selectedSectorId ? Number(selectedSectorId) : null);
  // Setor Cloud com a janela de 24 h fechada: texto, mídia e voz ficam travados (o servidor daria 409); só vale "Enviar modelo".
  const waComposerLocked = whatsAppComposerLocked({
    active: whatsAppCloudActive && replyChannel === 'whatsapp' && !selectedContact?.isGroup,
    messages,
    loading: isMessagesLoading,
  });
  const instagramAvailable = instagramActive && selectedContact?.hasInstagram === true;
  // Messenger: Página ativa no setor e o cliente com a identidade (PSID) dela.
  const messengerAvailable = messengerActive && selectedContact?.hasMessenger === true;
  // Site: o filtro só aparece com site ativo; a resposta vale sempre que o cliente falou pelo site.
  const { webchatActive } = useWebchatSite(selectedSectorId ? Number(selectedSectorId) : null);
  const webchatAvailable = selectedContact?.hasWebchat === true;
  const whatsappAvailable = selectedContact?.hasWhatsApp ?? Boolean((selectedContact?.number || '').trim());
  // Só escreve pelo Instagram e a conta caiu: faixa de reconexão no lugar do compositor do WhatsApp.
  const instagramNeedsReconnect = needsInstagramReconnect(selectedContact, { instagram: instagramActive });
  const timeline = useMemo(
    () => mergeConversationTimeline(messages, contactEmails.forbidden ? [] : contactEmails.emails),
    [messages, contactEmails.emails, contactEmails.forbidden],
  );
  useEffect(() => {
    setReplyChannel(defaultReplyChannel(selectedContact, { instagram: instagramAvailable, messenger: messengerAvailable }));
    setEmailReplyTo(null);
    setEmailDraftSeed(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContact?.id, emailOnly, instagramAvailable, messengerAvailable, webchatAvailable]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeletingContact, setIsDeletingContact] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [isArchivingContact, setIsArchivingContact] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);

  // Estados para microfone
  const [isMicrophoneAvailable, setIsMicrophoneAvailable] = useState(false);
  const [isCheckingMicrophone, setIsCheckingMicrophone] = useState(true);

  // Estado para AI toggle
  const [isTogglingAI, setIsTogglingAI] = useState(false);

  // Refs
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const loadingRef = useRef(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const shouldScrollToBottomRef = useRef(false);
  const isUserScrollingRef = useRef(false);
  const lastScrollTopRef = useRef(0);
  // Track pending messages for WebSocket matching
  const pendingMessagesRef = useRef<Map<number, { mediaType: string; sentAt: string }>>(new Map());
  const selectedContactRef = useRef<ContactData | null>(null);

  useEffect(() => {
    selectedContactRef.current = selectedContact;
  }, [selectedContact]);

  useEffect(() => {
    const handleRealtimeMessage = (data: any) => {
      const id = Number(data?.id);
      const contactID = Number(data?.contactID ?? data?.contactId);
      if (!id || !contactID) return;
      const incoming: Message = {
        id,
        content: data.content ?? null,
        mediaType: (data.mediaType ?? data.media_type ?? 'text').toLowerCase(),
        mediaUrl: data.mediaUrl ?? data.media_url ?? null,
        fileName: data.fileName ?? data.file_name ?? null,
        mimeType: data.mimeType ?? data.mime_type ?? null,
        sectorId: Number(data.sectorId ?? selectedSectorId ?? 0),
        contactID,
        sentAt: data.sentAt ?? data.sent_at ?? data.created_at ?? new Date().toISOString(),
        isSent: Boolean(data.isSent ?? data.is_sent ?? false),
        isRead: Boolean(data.isRead ?? data.is_read ?? false),
        senderName: data.senderName ?? data.sender_name ?? undefined,
        channel: data.channel === 'instagram' || data.channel === 'messenger' || data.channel === 'webchat' ? data.channel : undefined,
        status: 'sent',
      };

      if (!incoming.isSent && incoming.senderName) {
        setContacts(prev =>
          prev.map(c =>
            c.id === contactID
              ? { ...c, name: resolveContactDisplayName(c.name, incoming.senderName, c.number) }
              : c
          )
        );
        if (selectedContactRef.current?.id === contactID) {
          setSelectedContact(prev =>
            prev?.id === contactID
              ? { ...prev, name: resolveContactDisplayName(prev.name, incoming.senderName, prev.number) }
              : prev
          );
        }
      }

      if (selectedContactRef.current?.id !== contactID) return;

      setMessages(prev => {
        if (prev.some(m => m.id === incoming.id)) return prev;
        const withoutMatchedOptimistic = prev.filter(m => !matchesOptimisticSent(m, incoming));
        return [...withoutMatchedOptimistic, incoming]
          .sort((a, b) => dayjs(a.sentAt).valueOf() - dayjs(b.sentAt).valueOf());
      });
      shouldScrollToBottomRef.current = true;
    };

    const handleContactAiStatus = (data: any) => {
      const contactId = Number(data?.contactId);
      const aiActive = Number(data?.aiActive ?? 0);
      if (!contactId) return;
      setContacts(prev => prev.map(c => (c.id === contactId ? { ...c, aiActive } : c)));
      setSelectedContact(prev => (prev?.id === contactId ? { ...prev, aiActive } : prev));
    };

    const handleContactUpdate = (data: any) => {
      const contactId = Number(data?.id);
      if (!contactId) return;
      const spark = pickSparkFieldsFromRealtime(data);
      const apply = (c: ContactData): ContactData => ({
        ...c,
        name: resolveContactDisplayName(c.name, data.name as string | undefined, (data.number as string | undefined) || c.number),
        number: (data.number as string | undefined) || c.number,
        lastMessage: (data.lastMessage as string | undefined) ?? (data.last_message as string | undefined) ?? c.lastMessage,
        lastMessageTime: (data.lastMessageTime as string | undefined) ?? c.lastMessageTime,
        channel: spark.channel ?? c.channel,
        sparkChatId: spark.sparkChatId ?? c.sparkChatId,
        sparkEntrypointId: spark.sparkEntrypointId ?? c.sparkEntrypointId,
        sparkLinePhone: spark.sparkLinePhone ?? c.sparkLinePhone,
        sparkLineLabel: spark.sparkLineLabel ?? c.sparkLineLabel,
      });
      setContacts(prev => {
        if (!prev.some(c => c.id === contactId)) return prev;
        return prev.map(c => (c.id === contactId ? apply(c) : c));
      });
      setSelectedContact(prev => (prev?.id === contactId ? apply(prev) : prev));
    };

    const unsubMessage = ChatWebSocketService.on('message', handleRealtimeMessage);
    const unsubGroup = ChatWebSocketService.on('group_message', handleRealtimeMessage);
    const unsubAi = ChatWebSocketService.on('contact_ai_status', handleContactAiStatus);
    const unsubContact = ChatWebSocketService.on('contact', handleContactUpdate);
    return () => {
      unsubMessage();
      unsubGroup();
      unsubAi();
      unsubContact();
    };
  }, [selectedSectorId]);

  // Hooks
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const link = readChatLink(location.search);
    // /chat?contactId=&draft=&filter= — paridade com ChatWeb (HJ-02 / UX-02)
    if (link.contactId == null && !link.filter) return;
    if (link.filter) {
      setActiveFilter(link.filter);
      setShowContactsList(true);
      if (link.filter !== 'archived') setShowArchivedContacts(false);
    }
    if (link.contactId != null) {
      pendingDraftRef.current = link.draft;
      setPendingContactId(link.contactId);
    }
    navigate({ pathname: location.pathname, search: '' }, { replace: true });
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    if (!selectedContact || selectedContact.isGroup) return;
    rememberActiveContact(selectedContact);
  }, [selectedContact]);

  const selectedContactIdFromState = (location.state as { selectedContactId?: number } | null | undefined)?.selectedContactId;
  useEffect(() => {
    if (selectedContactIdFromState == null) return;
    const id =
      typeof selectedContactIdFromState === 'number'
        ? selectedContactIdFromState
        : parseInt(String(selectedContactIdFromState), 10);
    if (!Number.isFinite(id)) return;
    pendingDraftRef.current = null;
    setPendingContactId(id);
    navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: {} });
  }, [selectedContactIdFromState, location.pathname, location.search, navigate]);

  // Constantes
  const MESSAGES_PAGE_SIZE = 50;

  // Função para atualizar hasNewMessages
  const updateHasNewMessages = useCallback((newState: { [contactId: number]: boolean } | ((prev: { [contactId: number]: boolean }) => { [contactId: number]: boolean })) => {
    if (typeof newState === 'function') {
      setLocalHasNewMessages(newState);
      if (setHasNewMessages) {
        setHasNewMessages(newState(hasNewMessages));
      }
    } else {
      setLocalHasNewMessages(newState);
      if (setHasNewMessages) {
        setHasNewMessages(newState);
      }
    }
  }, [setHasNewMessages, hasNewMessages]);

  // Função para normalizar data
  function normalizeSentAtToLocal(sentAt: string) {
    const date = dayjs(sentAt);
    return date.isValid() ? date.toISOString() : dayjs().toISOString();
  }

  // Função para formatar tempo da mensagem
  function formatMessageTime(sentAt: string, isLocalMessage?: boolean) {
    try {
      const messageTime = dayjs(sentAt);
      if (!messageTime.isValid()) {
        return dayjs().format('HH:mm');
      }
      return messageTime.format('HH:mm');
    } catch (error) {
      console.error('Erro ao formatar tempo da mensagem:', error);
      return dayjs().format('HH:mm');
    }
  }

  // Carregar mensagens
  const loadMessages = useCallback(async (contactId: number, pageParam = 0, append = false) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    if (append) setIsLoadingMore(true);
    else if (pageParam === 0) setIsMessagesLoading(true);

    let previousScrollHeight = 0;
    let previousScrollTop = 0;
    if (append && messagesContainerRef.current) {
      previousScrollHeight = messagesContainerRef.current.scrollHeight;
      previousScrollTop = messagesContainerRef.current.scrollTop;
    }

    try {
      const offset = pageParam * MESSAGES_PAGE_SIZE;
      const response = await getMessagesByContactIdWithPagination(contactId, MESSAGES_PAGE_SIZE, offset);
      const newMessages = response.messages || [];

      setHasMore(response.hasMore);

      if (!append && response.contact) {
        const spark = pickSparkFieldsFromRealtime(response.contact as unknown as Record<string, unknown>);
        setSelectedContact(prev =>
          prev && prev.id === contactId
            ? {
                ...prev,
                number: response.contact?.number || prev.number,
                channel: spark.channel ?? prev.channel,
                sparkChatId: spark.sparkChatId ?? prev.sparkChatId,
                sparkEntrypointId: spark.sparkEntrypointId ?? prev.sparkEntrypointId,
                sparkLinePhone: spark.sparkLinePhone ?? prev.sparkLinePhone,
                sparkLineLabel: spark.sparkLineLabel ?? prev.sparkLineLabel,
              }
            : prev
        );
      }

      if (append) {
        setMessages(prev => {
          const updatedMessages = [
            ...newMessages.map(msg => ({
              ...msg,
              mediaUrl: coalesceMediaUrl(msg as Message & Record<string, unknown>) ?? msg.mediaUrl,
              sentAt: normalizeSentAtToLocal(msg.sentAt)
            })),
            ...prev
          ].sort((a, b) => dayjs(a.sentAt).valueOf() - dayjs(b.sentAt).valueOf());

          setTimeout(() => {
            if (messagesContainerRef.current && previousScrollHeight > 0) {
              const newScrollHeight = messagesContainerRef.current.scrollHeight;
              const scrollDifference = newScrollHeight - previousScrollHeight;
              messagesContainerRef.current.scrollTop = previousScrollTop + scrollDifference;
            }
          }, 100);

          return updatedMessages;
        });
      } else {
        setMessages(
          newMessages
            .map(msg => ({
              ...msg,
              mediaUrl: coalesceMediaUrl(msg as Message & Record<string, unknown>) ?? msg.mediaUrl,
              sentAt: normalizeSentAtToLocal(msg.sentAt)
            }))
            .sort((a, b) => dayjs(a.sentAt).valueOf() - dayjs(b.sentAt).valueOf())
        );
      }
      setInitialLoad(false);
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error);
    } finally {
      if (append) setIsLoadingMore(false);
      else if (pageParam === 0) setIsMessagesLoading(false);
      loadingRef.current = false;
    }
  }, [setMessages, setHasMore]);

  // Função melhorada para scroll para baixo
  const scrollToBottom = useCallback((behavior: 'smooth' | 'instant' = 'smooth') => {
    const scrollBehavior: ScrollBehavior = behavior === 'instant' ? 'auto' : behavior;
    try {
      const messagesContainer = messagesContainerRef.current;
      if (messagesContainer) {
        // Aguardar um pouco para garantir que o DOM foi atualizado
        requestAnimationFrame(() => {
          try {
            messagesContainer.scrollTo({
              top: messagesContainer.scrollHeight,
              behavior: scrollBehavior
            });
          } catch (error) {
            console.error('Erro ao fazer scroll:', error);
          }
        });
      }
    } catch (error) {
      console.error('Erro na função scrollToBottom:', error);
    }
  }, []);

  // Função para verificar se deve fazer scroll automático
  const shouldAutoScroll = useCallback(() => {
    try {
      const container = messagesContainerRef.current;
      if (!container) return false;

      // Se é carregamento inicial, sempre fazer scroll
      if (initialLoad) return true;

      // Se o usuário não está fazendo scroll manual, verificar se está próximo do final
      if (!isUserScrollingRef.current) {
        const threshold = 100;
        const isNearBottom = container.scrollTop >= (container.scrollHeight - container.clientHeight - threshold);
        return isNearBottom;
      }

      return false;
    } catch (error) {
      console.error('Erro na função shouldAutoScroll:', error);
      return false;
    }
  }, [initialLoad]);

  // Função para aguardar carregamento de imagens
  const waitForImagesLoad = useCallback(async () => {
    try {
      const container = messagesContainerRef.current;
      if (!container) return;

      const images = container.querySelectorAll('img');
      const imagePromises = Array.from(images).map(img => {
        if (img.complete) return Promise.resolve();

        return new Promise<void>((resolve) => {
          const onLoad = () => {
            try {
              img.removeEventListener('load', onLoad);
              img.removeEventListener('error', onLoad);
              resolve();
            } catch (error) {
              console.error('Erro ao remover event listeners:', error);
              resolve();
            }
          };

          try {
            img.addEventListener('load', onLoad);
            img.addEventListener('error', onLoad);

            // Timeout para não travar indefinidamente
            setTimeout(onLoad, 3000);
          } catch (error) {
            console.error('Erro ao adicionar event listeners:', error);
            resolve();
          }
        });
      });

      await Promise.all(imagePromises);
    } catch (error) {
      console.error('Erro na função waitForImagesLoad:', error);
    }
  }, []);

  // Função para fazer scroll inteligente
  const smartScrollToBottom = useCallback(async (force = false) => {
    try {
      if (!force && !shouldAutoScroll()) return;

      // Aguardar carregamento de imagens
      await waitForImagesLoad();

      // Fazer scroll
      scrollToBottom(initialLoad ? 'instant' : 'smooth');

      // Marcar que não é mais carregamento inicial
      if (initialLoad) {
        setInitialLoad(false);
      }
    } catch (error) {
      console.error('Erro na função smartScrollToBottom:', error);
    }
  }, [shouldAutoScroll, waitForImagesLoad, scrollToBottom, initialLoad]);

  // Enviar arquivo
  const handleFileUpload = async (file: File) => {
    try {
      if (!selectedContact || !selectedSectorId) return;

      const tempId = Date.now();
      const contactId = selectedContact.id;
      let nowLocal = dayjs();

      if (messages.length > 0) {
        const maxSentAt = messages.reduce((max, m) => {
          const t = dayjs(m.sentAt).valueOf();
          return t > max ? t : max;
        }, 0);
        if (nowLocal.valueOf() <= maxSentAt) {
          nowLocal = dayjs(maxSentAt + 1000);
        }
      }

      const sentAtString = nowLocal.format();

      // Marcar que deve fazer scroll
      shouldScrollToBottomRef.current = true;

      const mediaType = file.type.startsWith('image/') ? 'image' :
        file.type.startsWith('video/') ? 'video' :
          file.type.startsWith('audio/') ? 'audio' : 'document';

      // Create local preview URL for images and videos
      const localPreviewUrl = (mediaType === 'image' || mediaType === 'video')
        ? URL.createObjectURL(file)
        : null;

      // Optimistic update - add message immediately with 'sending' status
      const optimisticMessage: Message = {
        id: tempId,
        content: file.name,
        mediaType: mediaType,
        mediaUrl: localPreviewUrl,
        fileName: file.name,
        mimeType: file.type,
        sectorId: Number(selectedSectorId),
        contactID: contactId,
        sentAt: sentAtString,
        isSent: true,
        isRead: false,
        status: 'sending'
      };

      // Track this pending message for WebSocket matching
      pendingMessagesRef.current.set(tempId, { mediaType, sentAt: sentAtString });

      setMessages(prev => [...prev, optimisticMessage]);

      try {
        await sendFile({
          file,
          fileName: file.name,
          mediaType,
          caption: '',
          recipient: selectedContact.number,
          recipientPhone: selectedContact.number,
          contactId: selectedContact.id,
          sectorId: Number(selectedSectorId),
          isHuman: true,
          userId: currentUserId,
          isAnonymous: false,
          isSparkContact: isSparkContact(selectedContact)
        });
        setMessages(prev => markOptimisticAsSent(prev, tempId));
        pendingMessagesRef.current.delete(tempId);
      } catch (error) {
        console.error('Erro ao enviar arquivo:', error);
        // Update message status to 'error'
        setMessages(prev => prev.map(m =>
          m.id === tempId ? { ...m, status: 'error' as const, deliveryError: sendFailureText(error) } : m
        ));
      }
    } catch (error) {
      console.error('Erro na função handleFileUpload:', error);
    }
  };

  // Manipular seleção de arquivo
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
    // Limpar o input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Enviar áudio
  const handleSendAudio = async (audioBlob: Blob) => {
    if (!selectedContact || !selectedSectorId) return;

    const tempId = Date.now();
    const contactId = selectedContact.id;
    let nowLocal = dayjs();

    if (messages.length > 0) {
      const maxSentAt = messages.reduce((max, m) => {
        const t = dayjs(m.sentAt).valueOf();
        return t > max ? t : max;
      }, 0);
      if (nowLocal.valueOf() <= maxSentAt) {
        nowLocal = dayjs(maxSentAt + 1000);
      }
    }

    const sentAtString = nowLocal.format();

    // Create local preview URL for audio
    const localAudioUrl = URL.createObjectURL(audioBlob);

    // Marcar que deve fazer scroll
    shouldScrollToBottomRef.current = true;
    setIsRecording(false);

    // Optimistic update - add audio message immediately with 'sending' status
    const optimisticMessage: Message = {
      id: tempId,
      content: null,
      mediaType: 'audio',
      mediaUrl: localAudioUrl,
      fileName: `audio_${tempId}.wav`,
      mimeType: 'audio/wav',
      sectorId: Number(selectedSectorId),
      contactID: contactId,
      sentAt: sentAtString,
      isSent: true,
      isRead: false,
      status: 'sending'
    };

    // Track this pending message for WebSocket matching
    pendingMessagesRef.current.set(tempId, { mediaType: 'audio', sentAt: sentAtString });

    setMessages(prev => [...prev, optimisticMessage]);

    try {
      const spark = isSparkContact(selectedContact);
      await sendAudioMessage(
        audioBlob,
        selectedContact.number,
        selectedContact.id,
        Number(selectedSectorId),
        currentUserId,
        false,
        sentAtString,
        spark
      );
      setMessages(prev => markOptimisticAsSent(prev, tempId));
      pendingMessagesRef.current.delete(tempId);
      window.setTimeout(() => {
        try {
          URL.revokeObjectURL(localAudioUrl);
        } catch {
          /* ignore */
        }
      }, 180_000);
    } catch (error) {
      console.error('Erro ao enviar áudio:', error);
      // Update message status to 'error'
      setMessages(prev => prev.map(m =>
        m.id === tempId ? { ...m, status: 'error' as const, deliveryError: sendFailureText(error) } : m
      ));
    }
  };

  // Enviar mensagem
  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedContact || !selectedSectorId) {
      return;
    }

    const tempId = Date.now();
    const contactId = selectedContact.id;
    let nowLocal = dayjs();

    if (messages.length > 0) {
      const maxSentAt = messages.reduce((max, m) => {
        const t = dayjs(m.sentAt).valueOf();
        return t > max ? t : max;
      }, 0);
      if (nowLocal.valueOf() <= maxSentAt) {
        nowLocal = dayjs(maxSentAt + 1000);
      }
    }

    const sentAtString = nowLocal.format();
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Marcar que deve fazer scroll
    shouldScrollToBottomRef.current = true;
    const messageText = messageInput.trim();
    setMessageInput('');

    if (selectedContact) {
      updateHasNewMessages(prev => ({
        ...prev,
        [selectedContact.id]: false
      }));
    }

    const userId = currentUserId;
    if (userId === undefined) {
      console.error('Usuário não identificado');
      return;
    }

    // Optimistic update - add message immediately with 'sending' status
    const optimisticMessage: Message = {
      id: tempId,
      content: messageText,
      mediaType: 'text',
      mediaUrl: null,
      fileName: null,
      mimeType: null,
      sectorId: Number(selectedSectorId),
      contactID: contactId,
      sentAt: sentAtString,
      isSent: true,
      isRead: false,
      status: 'sending'
    };

    // Track this pending message for WebSocket matching
    pendingMessagesRef.current.set(tempId, { mediaType: 'text', sentAt: sentAtString });

    setMessages(prev => [...prev, optimisticMessage]);

    try {
      await sendMessage({
        text: messageText,
        to: selectedContact.number,
        recipientPhone: selectedContact.number,
        contactId: contactId,
        sectorId: Number(selectedSectorId),
        userId: userId,
        isAnonymous: false,
        sentAt: sentAtString,
        timezone,
        isSparkContact: isSparkContact(selectedContact)
      });
      setMessages(prev => markOptimisticAsSent(prev, tempId));
      pendingMessagesRef.current.delete(tempId);
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      // Update message status to 'error'
      setMessages(prev => prev.map(m =>
        m.id === tempId ? { ...m, status: 'error' as const, deliveryError: sendFailureText(error) } : m
      ));
    }
  };

  // Manipular tecla Enter
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (messageInput.trim()) {
        handleSendMessage();
      }
    }
  };

  // Callback para limpar indicador de não lido (chamado pelo ContactItem antes de onSelect)
  const handleClearNewMessage = useCallback((contactId: number) => {
    updateHasNewMessages(prev => ({ ...prev, [contactId]: false }));
    if (setUnreadCount) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  }, [updateHasNewMessages, setUnreadCount]);

  // Selecionar contato
  const handleContactSelect = (contact: ContactData) => {
    // Marcar como lido no backend (apenas para contatos não-grupo)
    const isGroup = !!(contact as any).isGroup;
    if (!isGroup && selectedSectorId) {
      markContactAsViewed(Number(selectedSectorId), contact.id);
    }
    setSelectedContact(contact);
    setShowContactsList(false);
    setPage(0);
    setInitialLoad(true);
    setMessages([]); // Limpar mensagens anteriores
    shouldScrollToBottomRef.current = true; // Forçar scroll para baixo
    loadMessages(contact.id, 0, false);
  };

  // Voltar para lista de contatos
  const handleBackToContacts = () => {
    setSelectedContact(null);
    setShowContactsList(true);
    setMessages([]);
  };

  // Renderizar mensagem
  const renderMessage = (message: Message) => {
    if (message.mediaType === 'internal_system') {
      return (
        <InternalSystemMessage
          key={message.id}
          id={message.id}
          content={message.content || ''}
          sentAt={message.sentAt}
          formatTime={formatMessageTime}
        />
      );
    }
    const isImage = message.mediaType === 'image';
    const isDocument = message.mediaType === 'document';
    const isAudio = message.mediaType === 'audio' || message.mediaType === 'voice';
    const isVideo = message.mediaType === 'video';
    const mediaUrl = coalesceMediaUrl(message as Message & Record<string, unknown>);
    const hasMediaUrl = !!mediaUrl;
    // Instagram: apagada pelo cliente não mostra o texto; editada ganha a marca.
    const metaView = metaMessageDisplay(message);
    const caption = metaView.deleted ? '' : getDisplayCaption(message.content);
    const hasContent = !!caption;

    return (
      <div key={message.id} className={`cmob-message ${message.isSent ? 'sent' : 'received'}`}>
        <div className="cmob-message-content">
          {message.channel && <div className="cmob-message-channel"><ChannelBadge channel={message.channel} label={message.accountLabel} /></div>}
          {isImage && hasMediaUrl && (
            <a className="cmob-message-image" href={mediaUrl!} target="_blank" rel="noopener noreferrer">
              <img src={mediaUrl!} alt="Imagem" />
            </a>
          )}
          {isImage && !hasMediaUrl && (
            <div className="cmob-message-image-placeholder">
              <ImageIcon />
              <span>📷 Imagem</span>
            </div>
          )}
          {isDocument && hasMediaUrl && (
            <a className="cmob-message-document" href={mediaUrl!} target="_blank" rel="noopener noreferrer" download={message.fileName || undefined}>
              <DocumentIcon />
              <span>{message.fileName || message.content || 'Documento'}</span>
            </a>
          )}
          {isDocument && !hasMediaUrl && (
            <div className="cmob-message-document">
              <DocumentIcon />
              <span>{message.content || message.fileName || 'Documento'}</span>
            </div>
          )}
          {isAudio && hasMediaUrl && (
            <div className="cmob-message-audio">
              <AudioMessage
                src={mediaUrl!}
                isSent={message.isSent}
                onCancel={message.status === 'sending' ? () => {
                  setMessages(prev => prev.filter(m => m.id !== message.id));
                } : undefined}
              />
            </div>
          )}
          {isAudio && !hasMediaUrl && (
            <div className="cmob-message-audio-placeholder">
              <MicIcon />
              <span>🎤 Áudio</span>
            </div>
          )}
          {isVideo && hasMediaUrl && (
            <div className="cmob-message-video">
              <video controls>
                <source src={mediaUrl!} type={message.mimeType || 'video/mp4'} />
              </video>
            </div>
          )}
          {isVideo && !hasMediaUrl && (
            <div className="cmob-message-video-placeholder">
              <span>🎬 Vídeo</span>
            </div>
          )}
          {/* Show text content for text messages OR for media with captions */}
          {(!isImage && !isDocument && !isAudio && !isVideo && hasContent) && (
            <span>{caption}</span>
          )}
          {metaView.deleted && <span className="cmob-message-deleted">{metaView.text}</span>}
          {metaView.edited && <span className="cmob-message-edited">editada</span>}
          {/* Show caption for media messages (skips placeholders like "[imagem]") */}
          {(isImage || isVideo) && hasContent && (
            <div className="cmob-message-caption">
              <span>{caption}</span>
            </div>
          )}
        </div>
        <WhatsAppDeliveryNote state={message.state} status={message.status} error={message.deliveryError} />
        <div className="cmob-message-info">
          <span className="cmob-message-time">
            {formatMessageTime(message.sentAt)}
          </span>
          {message.isSent && (
            <span className={`cmob-message-status ${message.status || 'sent'}`}>
              {message.status === 'sending' && <span className="cmob-status-spinner"></span>}
              {(message.status === 'sent' || !message.status) && '✓'}
              {message.status === 'error' && '⚠️'}
            </span>
          )}
        </div>
      </div>
    );
  };

  const contactsDeduped = useMemo(() => dedupeChatContactsForList(contacts), [contacts]);

  // Filtrar contatos (inclui filtros de operação Vision / HJ-02)
  const filteredContacts = useMemo(() => contactsDeduped.filter(contact => {
    const useServerSearch = debouncedSearch.trim().length >= 2;
    const matchesSearch =
      useServerSearch ||
      contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.number.includes(searchTerm);
    const matchesUser = !selectedFilterUser || contact.assignedTo === selectedFilterUser;
    const matchesTag = !selectedFilterTag || contact.tagId === selectedFilterTag;
    const matchesStatus = selectedStatus === 'all' || contact.contactStatus === selectedStatus;

    let matchesActiveFilter = true;
    if (activeFilter === 'archived') {
      matchesActiveFilter = contact.archived === true;
    } else if (activeFilter === 'favorites') {
      matchesActiveFilter = contact.isFavorite === true && !contact.archived;
    } else if (activeFilter === 'unread') {
      const isViewed = contact.is_viewed ?? true;
      matchesActiveFilter = (!isViewed || hasNewMessages[contact.id] === true) && !contact.archived;
    } else if (isOperationalFilter(activeFilter)) {
      // Mesmas regras/janelas dos totais da Hoje (HJ-02).
      matchesActiveFilter = matchesOperationalFilter(
        activeFilter, contact, new Date(), hasNewMessages[contact.id] === true,
      );
    } else if (activeFilter === 'whatsapp-groups') {
      matchesActiveFilter = contact.isGroup === true;
    } else if (activeFilter !== 'all') {
      matchesActiveFilter = !contact.archived;
    } else if (showArchivedContacts) {
      matchesActiveFilter = contact.archived === true;
    } else {
      matchesActiveFilter = !contact.archived;
    }

    if (isLidOnlyAddress(contact.number)) {
      return false;
    }

    const matchesChannel = activeFilter === 'whatsapp-groups' || matchesChannelFilter(contact, channelFilter);

    return matchesSearch && matchesUser && matchesTag && matchesStatus && matchesActiveFilter && matchesChannel;
  }), [
    channelFilter,
    contactsDeduped,
    debouncedSearch,
    searchTerm,
    selectedFilterUser,
    selectedFilterTag,
    selectedStatus,
    activeFilter,
    hasNewMessages,
    showArchivedContacts,
  ]);

  // Função para transferir contato
  const handleTransferContact = async () => {
    if (!selectedContact || !selectedUserId) return;

    setIsTransferLoading(true);
    try {
      await updateContact(selectedContact.id, {
        name: selectedContact.name,
        tagId: selectedContact.tagId,
        phoneWhatsapp: selectedContact.number,
        avatarUrl: selectedContact.avatarUrl,
        email: selectedContact.email,
        notes: selectedContact.notes,
        sectorId: selectedContact.sectorId,
        isActive: selectedContact.isActive,
        priority: selectedContact.priority,
        aiActive: selectedContact.aiActive,
        assignedTo: selectedUserId
      });

      // Atualizar o contato na lista
      setContacts(prev => prev.map(contact =>
        contact.id === selectedContact.id
          ? { ...contact, assignedTo: selectedUserId }
          : contact
      ));

      setShowTransferModal(false);
      setSelectedUserId(null);
      setShowHeaderMenu(false);
    } catch (error) {
      console.error('Erro ao transferir contato:', error);
    } finally {
      setIsTransferLoading(false);
    }
  };

  // Função para alterar tag
  const handleUpdateTag = async () => {
    if (!selectedContact) return;

    setIsTagLoading(true);
    try {
      await updateContact(selectedContact.id, {
        name: selectedContact.name,
        tagId: selectedTagId,
        phoneWhatsapp: selectedContact.number,
        avatarUrl: selectedContact.avatarUrl,
        email: selectedContact.email,
        notes: selectedContact.notes,
        sectorId: selectedContact.sectorId,
        isActive: selectedContact.isActive,
        priority: selectedContact.priority,
        aiActive: selectedContact.aiActive,
        assignedTo: selectedContact.assignedTo
      });

      // Atualizar o contato na lista
      setContacts(prev => prev.map(contact =>
        contact.id === selectedContact.id
          ? { ...contact, tagId: selectedTagId }
          : contact
      ));

      setSelectedContact(prev => prev ? { ...prev, tagId: selectedTagId } : null);
      setShowTagModal(false);
      setSelectedTagId(null);
      setShowHeaderMenu(false);
    } catch (error) {
      console.error('Erro ao alterar tag:', error);
    } finally {
      setIsTagLoading(false);
    }
  };

  // Função para excluir contato
  const handleDeleteContact = async () => {
    if (!selectedContact) return;

    setIsDeletingContact(true);
    try {
      await deleteContact(selectedContact.id);

      setContacts(prev => prev.filter(contact => contact.id !== selectedContact.id));

      if (selectedContact.archived) {
        setArchivedContactsCount(prev => (prev > 0 ? prev - 1 : 0));
      }

      handleBackToContacts();
      setShowDeleteModal(false);
      setShowHeaderMenu(false);
    } catch (error) {
      console.error('Erro ao excluir contato:', error);
    } finally {
      setIsDeletingContact(false);
    }
  };

  // Função para arquivar/desarquivar contato
  const handleArchiveContact = async () => {
    if (!selectedContact) return;

    setIsArchivingContact(true);
    try {
      const isCurrentlyArchived = selectedContact.archived || false;
      await archiveContact(selectedContact.id, !isCurrentlyArchived);

      // Remover o contato da lista atual (já que mudou de status)
      setContacts(prev => prev.filter(c => c.id !== selectedContact.id));

      // Atualizar a contagem de arquivados
      if (isCurrentlyArchived) {
        // Estava arquivado, agora não está mais
        setArchivedContactsCount(prev => prev - 1);
      } else {
        // Não estava arquivado, agora está
        setArchivedContactsCount(prev => prev + 1);
      }

      // Voltar para a lista de contatos
      handleBackToContacts();
      setShowArchiveModal(false);
      setShowHeaderMenu(false);
    } catch (error) {
      console.error('Erro ao arquivar/desarquivar contato:', error);
    } finally {
      setIsArchivingContact(false);
    }
  };

  // Função para alternar entre AI e Humano
  const handleToggleAI = async () => {
    if (!selectedContact || !selectedSectorId || isTogglingAI) return;

    setIsTogglingAI(true);
    try {
      const token = SessionService.getToken();
      if (!token) return;

      const newAIStatus = selectedContact.aiActive !== 1;
      await toggleContactAI(Number(selectedSectorId), selectedContact.id, newAIStatus, token);

      // Atualizar contato local
      setContacts(prev =>
        prev.map(c =>
          c.id === selectedContact.id
            ? { ...c, aiActive: newAIStatus ? 1 : 0 }
            : c
        )
      );

      // Atualizar contato selecionado
      setSelectedContact(prev =>
        prev ? { ...prev, aiActive: newAIStatus ? 1 : 0 } : null
      );

      console.log(`Contact ${selectedContact.id} AI status changed to: ${newAIStatus ? 'AI' : 'Human'}`);
    } catch (error) {
      console.error('Erro ao alterar status AI:', error);
    } finally {
      setIsTogglingAI(false);
    }
  };
  // Função para criar novo contato
  const handleCreateContact = async () => {
    if (!newContactName.trim() || !newContactNumber.trim() || !selectedSectorId) {
      setNewContactError('Informe o nome e o número.');
      return;
    }

    setIsCreatingContact(true);
    setNewContactError('');

    const toRow = (c: ApiContact): ContactData => ({
      id: c.id,
      name: c.name,
      number: c.number,
      avatarUrl: c.avatarUrl,
      email: c.email,
      notes: c.notes,
      tagId: c.tagId,
      sectorId: c.sectorId,
      isActive: c.isActive,
      priority: c.priority,
      contactStatus: c.contactStatus,
      aiActive: c.aiActive,
      aiAgentName: c.aiAgentName ?? null,
      assignedTo: c.assignedTo,
      createdAt: c.createdAt,
      is_viewed: c.is_viewed ?? c.isViewed,
      archived: c.archived,
      isGroup: false,
      isFavorite: c.isFavorite ?? false,
    });

    const sectorNum = Number(selectedSectorId);

    try {
      const newContact = await createContact({
        name: newContactName.trim(),
        phoneNumber: newContactNumber.trim(),
        email: '',
        sectorId: sectorNum
      });

      if (!newContact) {
        setNewContactError('Não foi possível criar o contato. Tente de novo.');
        return;
      }

      const row = toRow(newContact);
      if (row.archived) {
        setShowArchivedContacts(true);
        await loadContacts(sectorNum, false, true);
      } else {
        setContacts(prev => {
          const rest = prev.filter(x => x.id !== row.id);
          return dedupeChatContactsForList([row, ...rest]);
        });
      }

      setNewContactName('');
      setNewContactNumber('');
      setNewContactError('');
      setShowNewContactModal(false);
      setSearchTerm('');
      setSelectedFilterUser(null);
      setSelectedFilterTag(null);
      setSelectedStatus('all');
      handleContactSelect(row);
    } catch (error: unknown) {
      if (error instanceof DuplicateContactError) {
        const row = toRow(error.existingContact);
        if (row.archived) {
          setShowArchivedContacts(true);
          await loadContacts(sectorNum, false, true);
        } else {
          setShowArchivedContacts(false);
          await loadContacts(sectorNum, false, false);
        }
        setNewContactName('');
        setNewContactNumber('');
        setNewContactError('');
        setShowNewContactModal(false);
        setSearchTerm('');
        setSelectedFilterUser(null);
        setSelectedFilterTag(null);
        setSelectedStatus('all');
        handleContactSelect(row);
        return;
      }
      console.error('Erro ao criar contato:', error);
      setNewContactError(apiErrorMessage(error, 'Não foi possível criar o contato. Tente de novo.'));
    } finally {
      setIsCreatingContact(false);
    }
  };

  // Função para verificar se pode excluir contato
  const canDeleteContact = () => {
    if (!selectedContact || !isCurrentUserAdmin) return false;

    // Admin pode excluir qualquer contato
    if (isCurrentUserAdmin) return true;

    // Usuário comum só pode excluir se for o responsável
    return selectedContact.assignedTo === currentUserId;
  };

  // Função para verificar se pode transferir contato
  const canTransferContact = () => {
    if (!selectedContact) return false;

    // Admin pode transferir qualquer contato
    if (isCurrentUserAdmin) return true;

    // Usuário comum só pode transferir se for o responsável
    return selectedContact.assignedTo === currentUserId;
  };

  const loadMoreChatMobile = useCallback(async () => {
    if (!selectedSectorId || chatLoadLockRef.current || !chatHasMore) return;
    const sid = Number(selectedSectorId);
    chatLoadLockRef.current = true;
    setChatLoadingMore(true);
    try {
      const searchQ = debouncedSearch.length >= 2 ? debouncedSearch : undefined;
      const offset = chatNextOffsetRef.current;
      const res = await getContactsWithFilters(
        sid,
        false,
        showArchivedContacts,
        CHAT_CONTACTS_PAGE_SIZE,
        offset,
        'chat',
        searchQ,
        channelFilter
      );
      const raw = Array.isArray((res as any)?.data) ? (res as any).data : [];
      const chunk: ApiContact[] = [];
      for (const row of raw) {
        const c = mapApiContactToContact(row as Record<string, unknown>);
        if (c) chunk.push(c);
      }
      const newRows: ContactData[] = chunk.map((c: ApiContact) => ({
        id: c.id,
        name: c.name,
        number: c.number,
        avatarUrl: c.avatarUrl,
        email: c.email,
        notes: c.notes,
        tagId: c.tagId,
        sectorId: c.sectorId,
        isActive: c.isActive,
        priority: c.priority,
        contactStatus: c.contactStatus,
        aiActive: c.aiActive,
        aiAgentName: c.aiAgentName ?? null,
        assignedTo: c.assignedTo,
        createdAt: c.createdAt,
        updatedAt: c.updatedAt,
        is_viewed: c.is_viewed ?? c.isViewed,
        archived: c.archived,
        isFavorite: c.isFavorite,
        lastMessage: c.lastMessage,
        lastMessageTime: c.lastMessageTime,
        unreadCount: c.unreadCount,
        isGroup: c.isGroup ?? false,
      }));
      setContacts(prev => {
        const keepGroups = prev.filter(c => (c as any).isGroup);
        const people = prev.filter(c => !(c as any).isGroup);
        return dedupeChatContactsForList([...people, ...newRows, ...keepGroups] as any);
      });
      const pag = (res as any).pagination;
      chatNextOffsetRef.current = pag?.nextOffset ?? offset + chunk.length;
      setChatHasMore(Boolean(pag?.hasMore));
    } catch (e) {
      console.error('loadMoreChatMobile', e);
    } finally {
      chatLoadLockRef.current = false;
      setChatLoadingMore(false);
    }
  }, [selectedSectorId, chatHasMore, debouncedSearch, showArchivedContacts, channelFilter]);

  // Função para carregar contatos (inclui grupos WhatsApp) — primeira página + paginação no scroll
  const loadContacts = useCallback(async (sectorId: number, includeArchived: boolean = false, onlyArchived: boolean = false) => {
    // Também marca a primeira página como carregando: a lista vazia só mostra "nenhuma conversa" depois da resposta.
    setChatLoadingMore(true);
    try {
      chatNextOffsetRef.current = 0;
      const groupsRes = await getWhatsAppGroups(sectorId).catch(() => ({ data: [] }));
      const searchQ = debouncedSearch.length >= 2 ? debouncedSearch : undefined;
      const res = await getContactsWithFilters(
        sectorId,
        includeArchived,
        onlyArchived,
        CHAT_CONTACTS_PAGE_SIZE,
        0,
        'chat',
        searchQ,
        channelFilter
      );
      const raw = Array.isArray((res as any)?.data) ? (res as any).data : [];
      const contactsListRaw: ApiContact[] = [];
      for (const row of raw) {
        const c = mapApiContactToContact(row as Record<string, unknown>);
        if (c) contactsListRaw.push(c);
      }
      setContacts(apiAccToRows(contactsListRaw, sectorId, groupsRes));
      const pag = (res as any).pagination;
      chatNextOffsetRef.current = pag?.nextOffset ?? contactsListRaw.length;
      setChatHasMore(Boolean(pag?.hasMore));
      return contactsListRaw;
    } catch (error) {
      console.error('Erro ao carregar contatos:', error);
      try {
        const fallbackData = await getContacts(sectorId);
        if (Array.isArray(fallbackData)) {
          setContacts(dedupeChatContactsForList(fallbackData));
        } else if (fallbackData && 'data' in fallbackData) {
          setContacts(dedupeChatContactsForList((fallbackData as any).data || []));
        }
        setChatHasMore(false);
        return fallbackData;
      } catch (fallbackError) {
        console.error('Erro no fallback:', fallbackError);
        setContacts([]);
        setChatHasMore(false);
        return [];
      }
    } finally {
      setChatLoadingMore(false);
    }
  }, [debouncedSearch, channelFilter]);

  useEffect(() => {
    if (!pendingContactId || !selectedSectorId) return;
    const id = pendingContactId;
    setPendingContactId(null);

    const mapRawToContact = (raw: any): ContactData => ({
      id: raw.Id ?? raw.id,
      name: raw.Name ?? raw.name,
      number: raw.Number ?? raw.number,
      avatarUrl: raw.AvatarUrl ?? raw.avatarUrl ?? raw.avatarURL ?? null,
      email: raw.Email ?? raw.email ?? '',
      notes: raw.Notes ?? raw.notes ?? null,
      tagId: raw.TagId ?? raw.tagId ?? null,
      sectorId: raw.SectorId ?? raw.sectorId,
      isActive: raw.IsActive ?? raw.isActive ?? true,
      priority: raw.Priority ?? raw.priority ?? 'medium',
      contactStatus: raw.ContactStatus ?? raw.contactStatus ?? 'active',
      aiActive: raw.AiActive ?? raw.aiActive ?? raw.ai_active ?? 0,
      assignedTo: raw.AssignedTo ?? raw.assignedTo ?? null,
      createdAt: raw.CreatedAt ?? raw.createdAt,
      is_viewed: raw.IsViewed ?? raw.is_viewed ?? true,
      archived: raw.Archived ?? raw.archived ?? false,
      isFavorite: raw.IsFavorite ?? raw.isFavorite ?? false,
    });

    void (async () => {
      const openResolved = async (contact: ContactData) => {
        if (contact.sectorId !== Number(selectedSectorId)) {
          pendingDraftRef.current = null;
          return;
        }
        setShowArchivedContacts(!!contact.archived);
        await loadContacts(Number(selectedSectorId), false, !!contact.archived);
        setContacts(prev => (prev.some(c => c.id === contact.id) ? prev : [...prev, contact]));
        handleContactSelect(contact);
        if (pendingDraftRef.current) {
          setMessageInput(pendingDraftRef.current);
          pendingDraftRef.current = null;
        }
      };

      const all = [...contacts, ...(externalContacts || [])];
      const local = all.find(c => c.id === id);
      if (local) {
        await openResolved(local);
        return;
      }
      try {
        const result = await getContactById(id);
        if (!result) {
          pendingDraftRef.current = null;
          return;
        }
        const raw = (result as any).Data || (result as any).data || result;
        const contact = mapRawToContact(raw);
        if (contact.id) await openResolved(contact);
        else pendingDraftRef.current = null;
      } catch (e) {
        console.error('[ChatMobile] getContactById:', e);
        pendingDraftRef.current = null;
      }
    })();
  }, [pendingContactId, selectedSectorId, contacts, externalContacts, loadContacts]);

  // Inicialização
  useEffect(() => {
    const initializeData = async () => {
      try {
        const sectorId = SessionService.getSectorId();
        if (sectorId) {
          setSelectedSectorId(String(sectorId));

          // Carregar dados
          const [usersData, tagsData, countsRes] = await Promise.all([
            getAllUsers(SessionService.getCurrentUserId() || 0),
            getTags(Number(sectorId)),
            getContactCounts(Number(sectorId)),
          ]);

          // Processar dados dos usuários
          if (Array.isArray(usersData)) {
            setUsers(usersData);
          } else if (usersData && 'data' in usersData) {
            setUsers((usersData as any).data || []);
          }

          // Processar dados das tags
          if (Array.isArray(tagsData)) {
            setTags(tagsData);
          } else if (tagsData && 'data' in tagsData) {
            setTags((tagsData as any).data || []);
          }

          setArchivedContactsCount(countsRes?.archived ?? 0);
        }

        // Dados do usuário já estão sendo obtidos da sessão na inicialização dos estados

      } catch (error) {
        console.error('Erro ao inicializar dados:', error);
      }
    };

    initializeData();
  }, []);

  // Lista: primeira página + busca com debounce + alternar arquivados
  useEffect(() => {
    if (!selectedSectorId) return;
    void loadContacts(Number(selectedSectorId), false, showArchivedContacts);
  }, [showArchivedContacts, debouncedSearch, selectedSectorId, loadContacts]);

  // ── Polling: refresh messages every 3 seconds when a contact is open ──
  useEffect(() => {
    if (!selectedContact || !selectedSectorId) return;

    const contactId = selectedContact.id;

    const pollMessages = async () => {
      try {
        const result = await getMessagesByContactIdWithPagination(contactId, 50, 0);
        const freshMessages: Message[] = (result.messages || []).map((m: any) => {
          const row: Message = {
            id: m.id,
            content: m.content || null,
            mediaType: m.mediaType || 'text',
            mediaUrl: coalesceMediaUrl(m) ?? null,
            fileName: m.fileName || null,
            mimeType: m.mimeType || null,
            sectorId: m.sectorId || Number(selectedSectorId),
            contactID: m.contactID || contactId,
            sentAt: m.sentAt || new Date().toISOString(),
            isSent: m.isSent ?? false,
            isRead: m.isRead ?? false,
            channel: m.channel || undefined,
            accountLabel: m.accountLabel || undefined,
            state: m.state || undefined,
            deliveryError: m.deliveryError || undefined,
            status: 'sent' as const
          };
          return row;
        });

        setMessages(prev => {
          // Manter só mensagens otimistas que ainda não têm correspondente no servidor
          const sendingMsgs = prev.filter(isOptimisticOutgoing);
          const enriched = freshMessages.map(fm => enrichAudioMessageWithPendingBlob(fm, prev));
          const keptSending = sendingMsgs.filter(
            opt => !enriched.some(fm => matchesPendingOptimisticContent(opt, fm))
          );
          const merged = [...enriched, ...keptSending].sort(
            (a, b) => dayjs(a.sentAt).valueOf() - dayjs(b.sentAt).valueOf()
          );
          if (merged.length === prev.length) {
            const unchanged = prev.every(
              (m, i) =>
                merged[i]?.id === m.id &&
                merged[i]?.content === m.content &&
                merged[i]?.status === m.status &&
                merged[i]?.mediaUrl === m.mediaUrl &&
                merged[i]?.isRead === m.isRead &&
                merged[i]?.state === m.state &&
                merged[i]?.deliveryError === m.deliveryError &&
            merged[i]?.deliveryError === m.deliveryError &&
                merged[i]?.accountLabel === m.accountLabel
            );
            if (unchanged) return prev;
          }
          return merged;
        });
      } catch (err) {
        // Silent - will retry on next poll
      }
    };

    const intervalId = setInterval(pollMessages, 3000);
    return () => clearInterval(intervalId);
  }, [selectedContact?.id, selectedSectorId]);

  // Contact updates handled by parent component (CombinedMenu) via props


  // Disponibilidade do mic: NÃO abrir getUserMedia no mount (isso deixa o mic "em uso").
  useEffect(() => {
    let cancelled = false;
    const probe = async () => {
      try {
        const perms = navigator.permissions;
        if (perms?.query) {
          const status = await perms.query({ name: 'microphone' as PermissionName });
          if (!cancelled) {
            setIsMicrophoneAvailable(status.state !== 'denied');
            setIsCheckingMicrophone(false);
          }
          return;
        }
      } catch {
        /* Safari / browsers sem Permissions API */
      }
      if (!cancelled) {
        setIsMicrophoneAvailable(true);
        setIsCheckingMicrophone(false);
      }
    };
    void probe();
    return () => { cancelled = true; };
  }, []);

  // Efeito para scroll automático quando mensagens mudam
  useEffect(() => {
    if (shouldScrollToBottomRef.current) {
      smartScrollToBottom();
      shouldScrollToBottomRef.current = false;
    }
  }, [messages, smartScrollToBottom]);

  // Efeito para scroll inicial quando carrega mensagens
  useEffect(() => {
    if (!isLoadingMore && !isMessagesLoading && messages.length > 0 && initialLoad) {
      // Aguardar um pouco para garantir que o DOM foi renderizado
      setTimeout(() => {
        smartScrollToBottom(true);
        setInitialLoad(false); // Marcar que o carregamento inicial foi concluído
      }, 300); // Aumentar o tempo para garantir que tudo foi renderizado
    }
  }, [messages.length, isLoadingMore, isMessagesLoading, initialLoad, smartScrollToBottom]);

  // Efeito adicional para garantir scroll quando contato é selecionado
  useEffect(() => {
    if (selectedContact && messages.length > 0 && !isMessagesLoading) {
      // Aguardar um pouco mais para garantir que tudo foi renderizado
      setTimeout(() => {
        scrollToBottom('instant');
      }, 100);
    }
  }, [selectedContact?.id, messages.length, isMessagesLoading, scrollToBottom]);

  // Monitorar scroll do usuário
  useEffect(() => {
    const handleScroll = () => {
      const container = messagesContainerRef.current;
      if (!container) return;

      const currentScrollTop = container.scrollTop;
      const scrollDifference = Math.abs(currentScrollTop - lastScrollTopRef.current);

      // Se o usuário fez scroll manual (diferença significativa)
      if (scrollDifference > 10) {
        isUserScrollingRef.current = true;

        // Reset após um tempo
        setTimeout(() => {
          isUserScrollingRef.current = false;
        }, 1000);
      }

      lastScrollTopRef.current = currentScrollTop;

      // Mostrar/ocultar botão de scroll para baixo
      const threshold = 120;
      const isNearBottom = currentScrollTop >= (container.scrollHeight - container.clientHeight - threshold);
      setShowScrollToBottom(!isNearBottom);
    };

    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  return (
    <div className="cmob-container">
      {/* WebSocketStatus removed - using polling */}
      {/* Lista de contatos */}
      {showContactsList && (
        <div className="cmob-contacts-view">
          <div className="cmob-contacts-header">
            <button
              className="cmob-new-contact-btn"
              onClick={() => selectedSectorId && setShowNewContactModal(true)}
              disabled={!selectedSectorId}
            >
              + Novo contato
            </button>
            <button
              className="cmob-filters-btn"
              onClick={() => selectedSectorId && setShowFiltersModal(true)}
              disabled={!selectedSectorId}
            >
              Filtros
            </button>
          </div>

          <div className="cmob-search">
            <input
              type="text"
              placeholder={selectedSectorId ? "Buscar contatos…" : "Selecione uma conexão primeiro"}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={!selectedSectorId}
            />
          </div>
          {selectedSectorId && activeFilter !== 'whatsapp-groups' && (
            <div className="cmob-channel-filter-row">
              <ChannelFilterSelect value={channelFilter} onChange={setChannelFilter} options={channelFilterOptions({ instagram: instagramActive, messenger: messengerActive, webchat: webchatActive })} />
            </div>
          )}

          {selectedSectorId && visionEnabled && (
            <div className="cmob-op-filters" role="toolbar" aria-label="Filtros da operação">
              {(
                [
                  { id: 'all' as const, label: 'Todas' },
                  { id: 'ai-attending' as const, label: 'IA' },
                  { id: 'awaiting' as const, label: 'Aguardando' },
                  { id: 'needs-human' as const, label: 'Humanas' },
                  { id: 'risk-silent' as const, label: 'Risco' },
                  { id: 'unread' as const, label: 'Não lidas' },
                ] as const
              ).map((f) => (
                <button
                  key={f.id}
                  type="button"
                  className={`cmob-op-chip ${activeFilter === f.id ? 'is-active' : ''}`}
                  aria-pressed={activeFilter === f.id}
                  onClick={() => {
                    setActiveFilter(f.id);
                    setShowArchivedContacts(false);
                  }}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}

          {selectedSectorId && (
            <div className="cmob-archived-button-container">
              <button
                className={`cmob-archived-button ${showArchivedContacts ? 'active' : ''}`}
                onClick={() => setShowArchivedContacts(!showArchivedContacts)}
              >
                <div className="cmob-archived-content">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="21 8 21 21 3 21 3 8"></polyline>
                    <rect x="1" y="3" width="22" height="5"></rect>
                    <line x1="10" y1="12" x2="14" y2="12"></line>
                  </svg>
                  <span>Arquivadas</span>
                  <span className="cmob-archived-count">{archivedContactsCount}</span>
                </div>
              </button>
            </div>
          )}

          <div className="cmob-contacts">
            {!selectedSectorId ? (
              <div className="cmob-no-sector">
                <FiMenu size={48} style={{ opacity: 0.4, marginBottom: 12 }} />
                <span>Selecione uma conexão no menu principal para ver os contatos</span>
              </div>
            ) : (
              <Virtuoso
                style={{ height: '100%', minHeight: 0, flex: 1 }}
                data={filteredContacts}
                endReached={() => {
                  if (chatHasMore && !chatLoadingMore) void loadMoreChatMobile();
                }}
                components={{
                  EmptyPlaceholder: () =>
                    chatLoadingMore ? null : (
                      <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '13px' }}>
                        {channelFilter === 'email'
                          ? 'Nenhuma conversa por e-mail nesta conexão.'
                          : channelFilter === 'instagram'
                            ? 'Nenhuma conversa pelo Instagram nesta conexão.'
                          : channelFilter === 'messenger'
                            ? 'Nenhuma conversa pelo Messenger nesta conexão.'
                          : channelFilter === 'webchat'
                            ? 'Nenhuma conversa pelo Site neste setor.'
                          : debouncedSearch.length >= 2
                            ? 'Nenhuma conversa encontrada para essa busca.'
                            : 'Nenhuma conversa neste filtro.'}
                      </div>
                    ),
                  Footer: () =>
                    chatLoadingMore ? (
                      <div style={{ padding: '10px', textAlign: 'center', color: 'var(--text-tertiary)' }}>
                        <ImSpinner2 className="icon-spin" /> Carregando mais contatos…
                      </div>
                    ) : null,
                }}
                itemContent={(_i, contact) => (
                  <ContactItem
                    key={contact.id}
                    contact={{
                      id: contact.id,
                      name: contact.name,
                      number: contact.number,
                      email: contact.email,
                      avatarUrl: contact.avatarUrl,
                      lastMessage: contact.lastMessage,
                      lastMessageTime: contact.lastMessageTime,
                      tagId: contact.tagId,
                      tagColor: tags.find(t => t.id === contact.tagId)?.color,
                      isGroup: contact.isGroup,
                      isFavorite: contact.isFavorite,
                      contactStatus: contact.contactStatus,
                      aiActive: contact.aiActive,
                      aiAgentName: contact.aiAgentName,
                      unreadCount: contact.unreadCount,
                      lastMessageChannel: contact.lastMessageChannel,
                    }}
                    isSelected={selectedContact?.id === contact.id}
                    onSelect={() => handleContactSelect(contact)}
                    hasNewMessage={hasNewMessages?.[contact.id] || false}
                    onClearNewMessage={handleClearNewMessage}
                    onContextMenu={() => { }}
                  />
                )}
              />
            )}
          </div>
        </div>
      )}

      {/* Chat view */}
      {!showContactsList && selectedContact && (
        <div className="cmob-chat-view">
          {/* Header do chat */}
          <div className="cmob-header">
            <button className="cmob-back-button" onClick={handleBackToContacts}>
              <FiArrowLeft />
            </button>

            <div className="cmob-header-contact">
              <div className="cmob-header-avatar">
                <img
                  src={selectedContact.avatarUrl || '/user.png'}
                  alt={getContactPickerPrimaryLabel(selectedContact.name, selectedContact.number)}
                  onError={(e) => {
                    const el = e.target as HTMLImageElement;
                    el.style.display = 'none';
                    const fallback = el.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
                <div className="cmob-avatar-initials" style={{ display: 'none' }}>
                  {getContactPickerPrimaryLabel(selectedContact.name, selectedContact.number)
                    .split(' ')
                    .map(n => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2)}
                </div>
              </div>
              <div className="cmob-header-info">
                <div className="cmob-header-name">{getContactPickerPrimaryLabel(selectedContact.name, selectedContact.number)}</div>
                {(() => {
                  const sparkOrigin = formatSparkOriginLabel(selectedContact);
                  return sparkOrigin ? (
                    <div className="cmob-header-phone" title="WhatsApp oficial de entrada">
                      {sparkOrigin}
                    </div>
                  ) : null;
                })()}
              </div>
            </div>

            {isMendsVisionEnabled() && !selectedContact.isGroup && (
              <button
                type="button"
                className="cmob-header-menu-btn"
                title="Registrar"
                onClick={() => openRegisterConversation({
                  contactId: selectedContact.id,
                  contactName: getContactPickerPrimaryLabel(selectedContact.name, selectedContact.number),
                  conversationId: selectedContact.id,
                  sourceSurface: 'inbox',
                  screen: 'chat',
                  pathname: '/chat',
                  subjectKind: 'qualified_contact',
                })}
              >
                <FiMic />
              </button>
            )}

            <div
              className="cmob-ai-toggle"
              title={selectedContact.aiActive === 1
                ? (selectedContact.aiAgentName ? `${selectedContact.aiAgentName} (IA) está atendendo` : 'A IA está atendendo')
                : 'Você está atendendo'}
            >
              <span className={`cmob-ai-label ${selectedContact.aiActive === 1 ? 'active' : ''}`}>
                <FiCpu size={12} />
              </span>
              <button
                className={`cmob-ai-switch ${selectedContact.aiActive === 1 ? 'ai-active' : ''}`}
                onClick={handleToggleAI}
                disabled={isTogglingAI}
              >
                <span className="cmob-ai-switch-slider"></span>
              </button>
            </div>

            <button
              className="cmob-header-menu-btn"
              onClick={() => setShowHeaderMenu(!showHeaderMenu)}
            >
              <FiMoreVertical />
            </button>

            {showHeaderMenu && (
              <div className="cmob-header-menu">
                {canTransferContact() && (
                  <div className="cmob-header-menu-item" onClick={() => setShowTransferModal(true)}>
                    <FiUserPlus />
                    Transferir contato
                  </div>
                )}
                <div className="cmob-header-menu-item" onClick={() => setShowTagModal(true)}>
                  <FiTag />
                  Mudar etiqueta
                </div>
                <div className="cmob-header-menu-item" onClick={() => setShowArchiveModal(true)}>
                  <FiArchive />
                  {selectedContact?.archived ? 'Desarquivar' : 'Arquivar'}
                </div>
                {canDeleteContact() && (
                  <div className="cmob-header-menu-item" onClick={() => setShowDeleteModal(true)}>
                    <FiUsers />
                    Excluir contato
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Área de mensagens */}
          <div className="cmob-messages" ref={messagesContainerRef}>
            {isMessagesLoading && (
              <div className="cmob-messages-loading">
                <div className="cmob-loading-spinner"></div>
                <span>Carregando mensagens…</span>
              </div>
            )}

            {hasMore && !isMessagesLoading && (
              <button className="cmob-load-more" onClick={() => loadMessages(selectedContact.id, page + 1, true)}>
                {isLoadingMore ? (
                  <div className="cmob-loading-spinner" />
                ) : (
                  'Carregar mensagens anteriores'
                )}
              </button>
            )}

            {selectedContact && !selectedContact.isGroup && <EmailHistoryNotice {...contactEmails} />}
            {timeline.map(item => (item.kind === 'email'
              ? (
                <EmailTimelineBubble
                  key={item.key}
                  email={item.email}
                  formatTime={(iso) => formatMessageTime(iso)}
                  onReply={emailAvailable ? (email) => { setEmailReplyTo(email); setReplyChannel('email'); } : undefined}
                />
              )
              : renderMessage(item.message)))}

            <div ref={messagesEndRef} />

            {showScrollToBottom && (
              <button className="cmob-scroll-to-bottom" onClick={() => scrollToBottom('smooth')}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 16l-6-6h12l-6 6z" fill="currentColor" />
                </svg>
              </button>
            )}
          </div>

          {isMendsVisionEnabled() && !selectedContact.isGroup && (
            <InboxAiAssist
              contactId={selectedContact.id}
              contactName={getContactPickerPrimaryLabel(selectedContact.name, selectedContact.number)}
              onUseSuggestion={(text) => (replyChannel === 'email' ? setEmailDraftSeed(text) : setMessageInput(text))}
              onSendSuggestion={(text) => (replyChannel === 'email' ? setEmailDraftSeed(text) : setMessageInput(text))}
              onAssume={() => { if (selectedContact.aiActive === 1) void handleToggleAI(); }}
            />
          )}

          {/* Área de input */}
          <div className="cmob-input-area">
              <WhatsAppWindowNotice
                active={whatsAppCloudActive && replyChannel === 'whatsapp' && !selectedContact.isGroup}
                messages={messages}
                loading={isMessagesLoading}
                sectorId={selectedSectorId ? Number(selectedSectorId) : null}
                contactId={selectedContact.id}
                contactName={selectedContact.name}
              />
            {!selectedContact.isGroup && (emailAvailable || emailOnly || instagramAvailable || messengerAvailable || webchatAvailable) && (
              <ReplyChannelSwitch
                value={replyChannel}
                onChange={setReplyChannel}
                whatsappAvailable={whatsappAvailable}
                emailAvailable={emailAvailable}
                instagramAvailable={instagramAvailable}
                messengerAvailable={messengerAvailable}
                webchatAvailable={webchatAvailable}
              />
            )}
            {instagramNeedsReconnect && replyChannel !== 'email' && !selectedContact.isGroup ? (
              <InstagramReconnectNotice />
            ) : replyChannel === 'instagram' && instagramAvailable && !selectedContact.isGroup && selectedSectorId ? (
              <MetaReplyComposer
                sectorId={Number(selectedSectorId)}
                contactId={selectedContact.id}
                channel="instagram"
                refreshKey={messages.length > 0 ? messages[messages.length - 1].id : 0}
                onSent={(result, text) => {
                  const sent: Message = {
                    id: result.messageId,
                    content: text,
                    mediaType: 'text',
                    mediaUrl: null,
                    fileName: null,
                    mimeType: null,
                    sectorId: Number(selectedSectorId),
                    contactID: selectedContact.id,
                    sentAt: new Date().toISOString(),
                    isSent: true,
                    isRead: false,
                    channel: 'instagram',
                    status: 'sent',
                  };
                  setMessages(prev => (prev.some(m => m.id === sent.id)
                    ? prev
                    : [...prev, sent].sort((a, b) => dayjs(a.sentAt).valueOf() - dayjs(b.sentAt).valueOf())));
                  scrollToBottom('smooth');
                }}
              />
            ) : replyChannel === 'messenger' && messengerAvailable && !selectedContact.isGroup && selectedSectorId ? (
              <MetaReplyComposer
                sectorId={Number(selectedSectorId)}
                contactId={selectedContact.id}
                channel="messenger"
                refreshKey={messages.length > 0 ? messages[messages.length - 1].id : 0}
                onSent={(result, text) => {
                  const sent: Message = {
                    id: result.messageId,
                    content: text,
                    mediaType: 'text',
                    mediaUrl: null,
                    fileName: null,
                    mimeType: null,
                    sectorId: Number(selectedSectorId),
                    contactID: selectedContact.id,
                    sentAt: new Date().toISOString(),
                    isSent: true,
                    isRead: false,
                    channel: 'messenger',
                    status: 'sent',
                  };
                  setMessages(prev => (prev.some(m => m.id === sent.id)
                    ? prev
                    : [...prev, sent].sort((a, b) => dayjs(a.sentAt).valueOf() - dayjs(b.sentAt).valueOf())));
                  scrollToBottom('smooth');
                }}
              />
            ) : replyChannel === 'webchat' && webchatAvailable && !selectedContact.isGroup && selectedSectorId ? (
              <MetaReplyComposer
                sectorId={Number(selectedSectorId)}
                contactId={selectedContact.id}
                channel="webchat"
                refreshKey={messages.length > 0 ? messages[messages.length - 1].id : 0}
                onSent={(result, text) => {
                  const sent: Message = {
                    id: result.messageId,
                    content: text,
                    mediaType: 'text',
                    mediaUrl: null,
                    fileName: null,
                    mimeType: null,
                    sectorId: Number(selectedSectorId),
                    contactID: selectedContact.id,
                    sentAt: new Date().toISOString(),
                    isSent: true,
                    isRead: false,
                    channel: 'webchat',
                    status: 'sent',
                  };
                  setMessages(prev => (prev.some(m => m.id === sent.id)
                    ? prev
                    : [...prev, sent].sort((a, b) => dayjs(a.sentAt).valueOf() - dayjs(b.sentAt).valueOf())));
                  scrollToBottom('smooth');
                }}
              />
            ) : replyChannel === 'email' && !selectedContact.isGroup && selectedSectorId ? (
              <EmailReplyComposer
                sectorId={Number(selectedSectorId)}
                contactId={selectedContact.id}
                status={contactEmails.status}
                replyTo={emailReplyTo}
                onClearReplyTo={() => setEmailReplyTo(null)}
                initialBody={emailDraftSeed}
                onSent={(item) => { contactEmails.append(item); void contactEmails.reload(); scrollToBottom('smooth'); }}
              />
            ) : isRecording ? (
              <AudioRecorder
                onSend={handleSendAudio}
                onCancel={() => setIsRecording(false)}
              />
            ) : (
              <div className="cmob-input-container">
                <div className="cmob-input-actions">
                  <button
                    className="cmob-input-button"
                    title="Anexar imagem"
                    aria-label="Anexar imagem"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={!selectedSectorId || waComposerLocked}
                  >
                    <ImageIcon />
                  </button>
                  <button
                    className="cmob-input-button"
                    title="Anexar documento"
                    aria-label="Anexar documento"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={!selectedSectorId || waComposerLocked}
                  >
                    <DocumentIcon />
                  </button>
                  <button
                    className="cmob-input-button"
                    title="Gravar áudio"
                    aria-label="Gravar áudio"
                    onClick={() => isMicrophoneAvailable && setIsRecording(true)}
                    disabled={!isMicrophoneAvailable || !selectedSectorId || waComposerLocked}
                  >
                    <MicIcon />
                  </button>
                </div>

                <textarea
                  className="cmob-input"
                  placeholder={waComposerLocked ? WHATSAPP_TEMPLATE_ONLY_TEXT : selectedSectorId ? "Digite uma mensagem…" : "Selecione uma conexão para enviar mensagens"}
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyDown={handleKeyPress}
                  disabled={!selectedSectorId || waComposerLocked}
                  rows={1}
                />

                <button
                  className="cmob-send-button"
                  onClick={() => messageInput.trim() && handleSendMessage()}
                  disabled={!messageInput.trim() || !selectedSectorId || waComposerLocked}
                >
                  <SendIcon />
                </button>

                {/* Input de arquivo oculto */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/*,.pdf,.doc,.docx,.txt"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Estado vazio quando não há contato selecionado e não está mostrando lista */}
      {!showContactsList && !selectedContact && (
        <div className="cmob-empty">
          <div className="cmob-empty-content">
            <div className="cmob-empty-icon">💬</div>
            <h3>Selecione um contato</h3>
            <p>Escolha um contato na lista para começar a conversa</p>
            <button className="cmob-back-to-contacts" onClick={() => setShowContactsList(true)}>
              Ver contatos
            </button>
          </div>
        </div>
      )}

      {/* Modal de transferência */}
      {showTransferModal && (
        <div className="cmob-modal-overlay" onClick={() => setShowTransferModal(false)}>
          <div className="cmob-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cmob-modal-header">
              <h3>Transferir contato</h3>
              <button onClick={() => setShowTransferModal(false)}>×</button>
            </div>
            <div className="cmob-modal-content">
              <p>Escolha para quem transferir o contato:</p>
              <div className="cmob-user-list">
                {users.map(user => (
                  <div
                    key={user.id}
                    className={`cmob-user-item ${selectedUserId === user.id ? 'selected' : ''}`}
                    onClick={() => setSelectedUserId(user.id)}
                  >
                    <div className="cmob-user-avatar">
                      {user.name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)}
                    </div>
                    <span>{user.name}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="cmob-modal-actions">
              <button
                className="cmob-btn cmob-btn-secondary"
                onClick={() => setShowTransferModal(false)}
              >
                Cancelar
              </button>
              <button
                className="cmob-btn cmob-btn-primary"
                onClick={handleTransferContact}
                disabled={!selectedUserId || isTransferLoading}
              >
                {isTransferLoading ? 'Transferindo…' : 'Transferir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de tag */}
      {showTagModal && (
        <div className="cmob-modal-overlay" onClick={() => setShowTagModal(false)}>
          <div className="cmob-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cmob-modal-header">
              <h3>Mudar etiqueta</h3>
              <button onClick={() => setShowTagModal(false)}>×</button>
            </div>
            <div className="cmob-modal-content">
              <p>Escolha a etiqueta do contato:</p>
              <div className="cmob-tag-list">
                <div
                  className={`cmob-tag-item ${selectedTagId === null ? 'selected' : ''}`}
                  onClick={() => setSelectedTagId(null)}
                >
                  <span>Sem etiqueta</span>
                </div>
                {tags.map(tag => (
                  <div
                    key={tag.id}
                    className={`cmob-tag-item ${selectedTagId === tag.id ? 'selected' : ''}`}
                    onClick={() => setSelectedTagId(tag.id)}
                  >
                    <div
                      className="cmob-tag-color"
                      style={{ backgroundColor: tag.color }}
                    ></div>
                    <span>{tag.name}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="cmob-modal-actions">
              <button
                className="cmob-btn cmob-btn-secondary"
                onClick={() => setShowTagModal(false)}
              >
                Cancelar
              </button>
              <button
                className="cmob-btn cmob-btn-primary"
                onClick={handleUpdateTag}
                disabled={isTagLoading}
              >
                {isTagLoading ? 'Salvando…' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteContact}
        title="Excluir contato"
        message={selectedContact ? `Remover ${selectedContact.name} desta lista? Isso é diferente de arquivar a conversa.` : ''}
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        variant="danger"
        loading={isDeletingContact}
      />
      <ConfirmModal
        open={showArchiveModal}
        onClose={() => setShowArchiveModal(false)}
        onConfirm={handleArchiveContact}
        title={selectedContact?.archived ? 'Desarquivar contato' : 'Arquivar contato'}
        message={selectedContact ? `Quer mesmo ${selectedContact.archived ? 'desarquivar' : 'arquivar'} o contato ${selectedContact.name}?${!selectedContact.archived ? ' Ele vai para a seção de arquivadas.' : ''}` : ''}
        confirmLabel={selectedContact?.archived ? 'Desarquivar' : 'Arquivar'}
        cancelLabel="Cancelar"
        variant="default"
        loading={isArchivingContact}
      />

      {/* Modal de novo contato */}
      {showNewContactModal && (
        <div className="cmob-modal-overlay" onClick={() => setShowNewContactModal(false)}>
          <div className="cmob-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cmob-modal-header">
              <h3>Novo contato</h3>
              <button onClick={() => setShowNewContactModal(false)}>×</button>
            </div>
            <div className="cmob-modal-content">
              <div className="cmob-form-group">
                <label>Nome *</label>
                <input
                  type="text"
                  value={newContactName}
                  onChange={(e) => setNewContactName(e.target.value)}
                  placeholder="Nome do contato"
                />
              </div>
              <div className="cmob-form-group">
                <label>Telefone *</label>
                <input
                  type="text"
                  value={newContactNumber}
                  onChange={(e) => setNewContactNumber(e.target.value)}
                  placeholder="Número do WhatsApp com DDD"
                />
              </div>
              {newContactError && (
                <div className="cmob-error">{newContactError}</div>
              )}
            </div>
            <div className="cmob-modal-actions">
              <button
                className="cmob-btn cmob-btn-secondary"
                onClick={() => setShowNewContactModal(false)}
              >
                Cancelar
              </button>
              <button
                className="cmob-btn cmob-btn-primary"
                onClick={handleCreateContact}
                disabled={isCreatingContact}
              >
                {isCreatingContact ? 'Criando…' : 'Criar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatMobile;
